// Orders, tabs, order history, payments, and kitchen tickets.
import type { AddOnChoice, OrderItemAddOn } from './menu';
import type { VatMode } from './tenant';

export type ServiceTable = {
  id: string;
  name: string;
  capacity: number;
  area: string;
  status: 'free' | 'occupied' | 'reserved' | 'dirty';
  /** Lucide icon name. Empty = default chair glyph. */
  icon: string;
  sort: number;
};

/** Render a line quantity for display. Whole numbers show plainly ("3");
 *  half-plate quantities use the ½ glyph ("½", "1½", "3½"). Any other
 *  fraction (shouldn't occur) falls back to a trimmed decimal. Shared so the
 *  POS, kitchen board, receipts, and history all format qty identically.
 *
 *  Pass `ascii: true` for thermal printing — most ESC/POS codepages lack the
 *  ½ glyph, so halves render as "1/2" / "3 1/2" instead. */
export function formatQty(qty: number, ascii = false): string {
  const whole = Math.floor(qty);
  const frac = qty - whole;
  if (Math.abs(frac) < 1e-6) return String(whole);
  if (Math.abs(frac - 0.5) < 1e-6) {
    if (ascii) return whole === 0 ? '1/2' : `${whole} 1/2`;
    return whole === 0 ? '½' : `${whole}½`;
  }
  return String(Math.round(qty * 100) / 100);
}

// 'staff_meal' is a terminal status, not a lifecycle stage: food taken by a
// member of staff at no charge. It is deliberately NOT 'closed', because every
// sales figure in the API keys off `status = 'closed'` — so a staff meal is
// excluded from revenue by construction rather than by remembering to filter.
export type OrderStatus = 'open' | 'closed' | 'cancelled' | 'staff_meal';

export type KitchenStatus = 'pending' | 'in_progress' | 'ready' | 'served';

export type OrderItemRow = {
  id: string;
  order_id: string;
  menu_item_id: string;
  menu_item_name: string;
  qty: number;
  /** FOLDED price: the item's own price plus every chosen add-on, per one unit.
   *  Use this (or line_cents) for all money math. */
  unit_price_cents: number;
  /** The item's own price alone, before add-ons. Lets a receipt print the dish
   *  and its add-ons as separate lines while the totals stay in unit_price_cents.
   *  Optional so an older API response stays type-compatible. */
  base_price_cents?: number;
  line_cents: number;
  /** Itemised breakdown of what unit_price_cents folds in. Always an array from
   *  a current API; optional so older responses still typecheck. */
  add_ons?: OrderItemAddOn[];
  /** @deprecated Speculative jsonb column, never populated. See add_ons. */
  modifiers: unknown;
  notes: string;
  kitchen_status: KitchenStatus;
  sent_to_kitchen_at?: string | null;
  ready_at?: string | null;
  served_at?: string | null;
  voided_at?: string | null;
  void_reason?: string | null;
  created_at: string;
};

export type Order = {
  id: string;
  service_table_id?: string | null;
  service_table_name?: string | null;
  // Free-text name for a walk-in / "Unknown +" tab (no real table). Empty
  // string when unnamed; on a real table service_table_name takes priority.
  table_label?: string;
  // True for the shared staff-meals tab (see OrderStatus). Never combined with
  // a service_table_id — a staff meal does not occupy a table. Who ate what is
  // deliberately not recorded (0085).
  is_staff_meal?: boolean;
  /** Fulfilment channel. Independent of service_table_id — see 0081. */
  order_type: OrderType;
  status: OrderStatus;
  opened_by_user_id: string;
  opened_at: string;
  closed_at?: string | null;
  notes: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  service_charge_cents: number;
  total_cents: number;
  live_subtotal_cents: number;
  items?: OrderItemRow[];
  // Per-status non-voided item counts. Populated by list+get; lets the
  // floor + tab pages show "all served · settle pending" style labels
  // without loading every order's full item array.
  items_pending: number;
  items_in_progress: number;
  items_ready: number;
  items_served: number;
  items_total: number;
  paid_cents: number;
};

/** Fulfilment channel — see migration 0081. Independent of whether the serve
 *  holds a table: a delivery order has no table and is not a takeaway, and a
 *  seated tab can be switched to takeaway without giving up its table. */
export type OrderType = 'dine_in' | 'takeaway' | 'delivery';

/** Human label for a fulfilment channel. */
export function orderTypeLabel(t: OrderType | null | undefined): string {
  switch (t) {
    case 'takeaway':
      return 'Takeaway';
    case 'delivery':
      return 'Delivery';
    default:
      return 'Dine-in';
  }
}

/**
 * Resolve a tab's display name. Priority: a real table's registry name wins;
 * otherwise the free-text walk-in label; otherwise the fallback ("Walk-in" on
 * the floor/kitchen/history, "Take-away" inside the tab).
 *
 * Still here, and still the right function for "which table is this?" — the
 * move/merge dialog and the history table filter both genuinely ask that. For
 * "what should this serve be called?", prefer resolveServeLabel below.
 */
export function resolveTableLabel(
  o: { service_table_name?: string | null; table_label?: string | null },
  fallback = 'Walk-in',
): string {
  return o.service_table_name ?? (o.table_label?.trim() || fallback);
}

/**
 * The name to show for a serve: a real table's name, else the free-text
 * walk-in name, else the fulfilment channel.
 *
 * This replaces the per-screen fallback strings. The same table-less serve used
 * to be called "Walk-in" on the floor, "Unknown" on its tile, "Take-away" in
 * the tab header and "—" in a report — four names for one thing, on adjacent
 * screens, none of which told the kitchen whether to box it. Falling back to
 * the order type means the answer is the same everywhere and is a fact about
 * the serve rather than a property of whichever screen is asking.
 */
export function resolveServeLabel(o: {
  service_table_name?: string | null;
  table_label?: string | null;
  order_type?: OrderType | null;
}): string {
  return (
    o.service_table_name ?? (o.table_label?.trim() || orderTypeLabel(o.order_type ?? 'dine_in'))
  );
}

export type TabState = {
  key:
    | 'empty'
    | 'ordering'
    | 'cooking'
    | 'ready-to-serve'
    | 'served-settle'
    | 'served-partial-paid'
    | 'new-items-after-send';
  /** Short label, lowercase. Suitable for a pill. */
  label: string;
  /** One-line description for tooltip / secondary text. */
  hint: string;
  /** Tone bucket for styling. */
  tone: 'neutral' | 'info' | 'warn' | 'action' | 'success';
};

/**
 * Derive a single, action-oriented label for an OPEN tab from its item-status
 * counts and paid amount. Priority is ordered by who needs to do something
 * next: server action (ready to serve) > waiting on kitchen > waiting on the
 * cashier (settle) > nothing yet. Returns null for closed/cancelled orders —
 * those have their own status field.
 */
export function deriveTabState(o: Order): TabState | null {
  if (o.status !== 'open') return null;

  const total = o.items_total ?? 0;
  const pending = o.items_pending ?? 0;
  const inProg = o.items_in_progress ?? 0;
  const ready = o.items_ready ?? 0;
  const served = o.items_served ?? 0;
  const inFlight = pending + inProg + ready;

  if (total === 0) {
    return { key: 'empty', label: 'new tab', hint: 'no items yet', tone: 'neutral' };
  }
  if (ready > 0) {
    return {
      key: 'ready-to-serve',
      label: `${ready} ready · serve`,
      hint: 'kitchen has items ready to be served',
      tone: 'action',
    };
  }
  if (pending > 0 && served > 0 && inProg === 0) {
    return {
      key: 'new-items-after-send',
      label: `${pending} new · send to kitchen`,
      hint: 'new items added to a partly-served tab',
      tone: 'warn',
    };
  }
  if (inProg > 0) {
    return {
      key: 'cooking',
      label: pending > 0 ? `${inProg} cooking · ${pending} not sent` : `${inProg} cooking`,
      hint: 'kitchen is working on items',
      tone: 'info',
    };
  }
  if (pending > 0) {
    return {
      key: 'ordering',
      label: `${pending} not sent`,
      hint: 'items added but not sent to kitchen yet',
      tone: 'warn',
    };
  }
  // All non-voided items are served (inFlight === 0, served === total).
  if (inFlight === 0 && served === total) {
    if ((o.paid_cents ?? 0) === 0) {
      return {
        key: 'served-settle',
        label: 'all served · settle',
        hint: 'every item served — collect payment to close',
        tone: 'action',
      };
    }
    return {
      key: 'served-partial-paid',
      label: 'all served · part paid',
      hint: 'partial payment recorded — collect balance to close',
      tone: 'action',
    };
  }
  return { key: 'empty', label: 'open tab', hint: '', tone: 'neutral' };
}

/** Add-items vars: every item MUST carry a client-generated UUID (`id`).
 *  The server inserts with ON CONFLICT DO NOTHING, so retries and offline
 *  replays of the same payload are exactly-once. */
export type AddOrderItemsVars = {
  orderId: string;
  items: {
    id: string;
    menu_item_id: string;
    qty: number;
    notes?: string;
    /** Chosen add-ons. Each carries its own client-minted id, so a replay is
     *  exactly-once for the add-ons as well as the parent line. */
    add_ons?: AddOnChoice[];
    modifiers?: unknown;
  }[];
  // When set, a single optimistic line is inserted into the cache immediately
  // (used by the tab picker so rapid taps show up without the round-trip).
  // unit_price_cents is the FOLDED price; base_price_cents is the dish alone,
  // needed so a later add-on edit re-folds onto the base and not onto itself.
  optimistic?: { menu_item_name: string; unit_price_cents: number; base_price_cents?: number };
  /** Priced add-on rows for the optimistic line only — `items[].add_ons` sends
   *  ids and the server prices them; the cache needs names and amounts to show
   *  the sub-lines before the refetch lands. */
  optimisticAddOns?: OrderItemAddOn[];
};

export type HistoryPayment = {
  id: string;
  method: PaymentMethod;
  amount_cents: number;
  reference_no: string;
  // True when the payment may be flipped cash↔online (cash/online method on a
  // still-open shift). House-tab charges and closed-shift payments are false.
  reclassifiable: boolean;
};

export type HistoryOrder = {
  id: string;
  service_table_id?: string | null;
  service_table_name?: string | null;
  table_label?: string;
  order_type?: OrderType;
  opened_at: string;
  closed_at?: string | null;
  notes: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  service_charge_cents: number;
  total_cents: number;
  item_count: number;
  items: OrderItemRow[];
  payments: HistoryPayment[];
};

/** One credit (house-tab) settlement recorded on the day being viewed: money
 *  collected against a sale closed earlier, so it sits alongside the day's
 *  serves and never inside its sales total. Absent when a table filter is
 *  applied — collections belong to a tab, not a table. */
export type HistoryCreditCollection = {
  id: string;
  house_tab_id: string;
  house_tab_name: string;
  method: PaymentMethod;
  amount_cents: number;
  reference_no: string;
  recorded_at: string;
};

export type OrderHistoryResp = {
  /** First day of the window — equals `from`. Kept for the History screen. */
  date: string;
  /** Inclusive first/last day the response covers, echoed back. A single-day
   *  request (?date=) collapses to from === to; the report builder asks for a
   *  span (?from=&to=) so a month is one request rather than a call per day. */
  from?: string;
  to?: string;
  timezone: string;
  orders: HistoryOrder[];
  credit_collections?: HistoryCreditCollection[];
};

export type AdjustmentType = 'discount' | 'service_charge' | 'tax_override';

export type OrderAdjustment = {
  id: string;
  order_id: string;
  type: AdjustmentType;
  amount_cents: number;
  reason: string;
  applied_by_user_id: string;
  approved_by_user_id: string;
  created_at: string;
  /** Set only on a category promotion (migration 0078); absent on a manual
   *  discount or a QR reward. Its presence is what identifies a promo row. */
  menu_category_id?: string | null;
  /** Category name, denormalised so a client can label the row without
   *  re-joining the catalog. */
  category_name?: string;
  /** The percentage the amount came from, in basis points. Recorded on the row
   *  so a later percentage change can never rewrite a closed bill. */
  percent_bp?: number | null;
};

/** True when this adjustment is an automatic category promotion rather than a
 *  discount someone applied by hand. The two are the same row shape on purpose
 *  (so headroom, the quote and every report treat them identically), so the
 *  category id is the only thing that tells them apart. */
export function isCategoryPromotion(a: OrderAdjustment): boolean {
  return a.type === 'discount' && !!a.menu_category_id;
}

/** Label for an adjustment row: "Breakfast 10%" for a promotion, otherwise the
 *  caller's own reason label. Shared so web and mobile can't word it
 *  differently. */
export function promotionLabel(a: OrderAdjustment): string | null {
  if (!isCategoryPromotion(a)) return null;
  const pct = a.percent_bp != null ? `${trimTrailingZeros(a.percent_bp / 100)}%` : '';
  return [a.category_name || 'Category', pct].filter(Boolean).join(' ');
}

function trimTrailingZeros(n: number): string {
  return String(Number(n.toFixed(2)));
}

// Wire-level payment method. New rows write 'cash' / 'online' / 'house_tab';
// the older values still appear on historical rows (esewa / khalti / card /
// other). UI consumers display anything outside the canonical set as
// "Online" — see SettleModal.METHOD_DISPLAY.
export type PaymentMethod =
  | 'cash'
  | 'online'
  | 'bank'
  | 'esewa'
  | 'khalti'
  | 'card'
  | 'other'
  | 'house_tab';

export type Payment = {
  id: string;
  order_id: string;
  method: PaymentMethod;
  amount_cents: number;
  reference_no: string;
  house_tab_id?: string | null;
  house_tab_name?: string | null;
  recorded_by_user_id: string;
  recorded_at: string;
};

export type SettleQuote = {
  subtotal_cents: number;
  discount_cents: number;
  service_charge_cents: number;
  tax_cents: number;
  total_cents: number;
  paid_cents: number;
  balance_cents: number;
  service_charge_pct: string;
  vat_pct: string;
  vat_mode: VatMode;
};

export type KitchenTicket = {
  item_id: string;
  order_id: string;
  service_table_name?: string | null;
  table_label?: string;
  /** Whether this is going in a box. A takeaway plated, or a dine-in boxed, is
   *  a remake either way — so the cook is told rather than left to infer it
   *  from a missing table name. */
  order_type?: OrderType;
  menu_item_name: string;
  qty: number;
  /** Add-ons on this line, to render indented under the item name. Never their
   *  own ticket — an add-on belongs to the dish the cook is making. */
  add_ons?: OrderItemAddOn[];
  /** @deprecated Unused jsonb. See add_ons. */
  modifiers: unknown;
  notes: string;
  kitchen_status: 'in_progress' | 'ready';
  sent_to_kitchen_at?: string | null;
  ready_at?: string | null;
  /** Prep outlet this ticket was routed to (stamped at send). Null for tickets
   *  sent before outlets existed — those fold onto the default outlet's board. */
  outlet_id?: string | null;
  outlet_name?: string | null;
};
